import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { z } from "zod";
import { NO_COLOR, type Color } from "@salestrak/ui";
import {
  FIELD_GROUP_PREFIX,
  NO_OUTCOME_KEY,
  fieldGroupToken,
  isFieldGroupToken,
  parseFieldGroupToken,
  type FieldDateGrain,
  type MeasureFn,
} from "@salestrak/widgets";
import { asOptionColor } from "#app/components/custom-fields/option-visuals";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { isStageType, useMemberEntityResolver, type StageType } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { stageTypeLabel } from "#app/lib/entity-visuals";
import { normalizeOutcomeTone, OUTCOME_TONE_CONFIG } from "#app/lib/outcome-tone";
import { STAGE_TYPE_COLOR, STAGE_TYPE_ORDER } from "#app/lib/stage-type";
import { useActivityGroupCounts } from "#app/widgets/definitions/shared/use-activity-group-counts";
import { foldMeasure, isDateField, type FieldMeasure } from "./widget-measure";

/**
 * The sentinel the server emits for a NULL dimension value (an issue with no
 * stage/type/workflow/team assignee) — `issues.service.ts` groupCounts. THE one
 * shared constant both sides key on; the client must never invent its own (a
 * mismatched `"__none__"` made untyped series read as corrupt "Unknown …"). A
 * bucket carrying this key means "No <dimension>", not unknown/corrupt data.
 */
export const UNASSIGNED_DIMENSION_KEY = "__unassigned__";

/**
 * The bucket every id that resolves to no workspace entity folds into — a
 * member removed from the workspace, a team that no longer exists (a member id
 * the directory has not answered for YET passes through here for one round
 * trip). The work is real, so it stays counted, but the id carries no name to
 * rank by: left apart, each one arrives as its own identical "Unknown" row
 * scattered through the ranking. Distinct from `UNASSIGNED_DIMENSION_KEY`,
 * which means the issue carries no value for the dimension at all.
 */
export const UNRESOLVED_DIMENSION_KEY = "__unresolved__";

/** The built-in group-by dimensions count widgets can slice on (each maps to an
 * allowed server `groupBy`). The tuple is THE shared vocabulary — every widget
 * config schema builds its dimension schema from it; never restate the literals. */
export const WIDGET_DIMENSIONS = [
  "type",
  "workflow",
  "stage",
  "stageType",
  "team",
  "assignee",
  "age",
  "outcome",
  "source",
] as const;
export type BuiltinWidgetDimension = (typeof WIDGET_DIMENSIONS)[number];

/**
 * A workspace custom field as a dimension: the server's own `groupBy` token
 * (`values.<fieldId>`, or `values.<fieldId>.<grain>` for a date field — see
 * `@salestrak/widgets` field-measures). A config stores the grainless token
 * straight from the Group by select; `useGroupDimension` adds the grain a date
 * field needs.
 */
export type FieldWidgetDimension = `${typeof FIELD_GROUP_PREFIX}${string}`;

export type WidgetDimension = BuiltinWidgetDimension | FieldWidgetDimension;

/** What the Group by select stores: a built-in dimension, or a custom field's
 *  token. The select lists both in one flat list — the built-ins, then the
 *  workspace's groupable fields under their type headings. */
export type GroupByChoice = WidgetDimension;

export function isGroupByChoice(value: unknown): value is GroupByChoice {
  if (typeof value !== "string") return false;
  return (WIDGET_DIMENSIONS as readonly string[]).includes(value) || parseFieldGroupToken(value) !== null;
}

/** THE config schema for every dimension-sliced widget's `groupBy`; never
 *  `z.enum(WIDGET_DIMENSIONS)`, which rejects a field token. */
export const groupBySchema = z.custom<GroupByChoice>(isGroupByChoice);

export function isFieldDimension(dimension: WidgetDimension): dimension is FieldWidgetDimension {
  return isFieldGroupToken(dimension);
}

/**
 * The dimension a widget's Group by resolves to: a built-in as itself; a field
 * token as the token the server buckets by — a date field bucketed by month,
 * the one grain a categorical chart holds. Null when the token names a field
 * the workspace no longer has: nothing to ask the server, and the card says so.
 */
export function useGroupDimension(groupBy: GroupByChoice): WidgetDimension | null {
  const { customFields } = useWorkspaceData();
  return useMemo(() => {
    if (!isFieldDimension(groupBy)) return groupBy;
    const token = parseFieldGroupToken(groupBy);
    const field = token
      ? customFields.find((candidate) => candidate.customFieldId === token.fieldId)
      : undefined;
    if (!field) return null;
    return fieldGroupToken(field.customFieldId, isDateField(field) ? "month" : null) as FieldWidgetDimension;
  }, [groupBy, customFields]);
}

/**
 * `stageType` is the stage dimension FOLDED to the four categories (pending /
 * in progress / done / canceled). The server groups by stage id — a stage's
 * category is a workspace fact the client already holds on every stage row —
 * so the reader sums the per-stage counts into one slice per category and
 * carries the member stage ids on the slice (`ids`), which is exactly what a
 * drilldown needs: `stageId: { in }`. The slices keep PIPELINE order, not
 * rank order — pending before done is the reading, whatever the counts.
 */
export function isStageTypeDimension(dimension: WidgetDimension): dimension is "stageType" {
  return dimension === "stageType";
}

/**
 * `outcome` is the ONE dimension that does not count projects. An outcome is
 * recorded on an Update, and an Update is an activity row — so the count comes
 * from `activityGroupCounts`, over the ACTIVITY fold, and the returned keys are
 * reason ids. Every other dimension counts issues. This predicate is what the
 * whole slice pipeline branches on; never compare the literal inline. A type
 * predicate, so the false branch narrows to the issue-counting dimensions.
 */
export function isOutcomeDimension(dimension: WidgetDimension): dimension is "outcome" {
  return dimension === "outcome";
}

/** One slice: a category in the chosen dimension and its figure. `total` is
 * what the chart plots — the scoped project count, or the measured field
 * aggregate when the widget carries a field measure; `count` is the projects
 * behind it either way. `color` is the entity-owned palette color name
 * (stage/team/type/workflow icon color, follow-up deco color, a select option's
 * color) — charts wear it via `useCategoricalColors`. `null` means the category
 * has no owned color (a user) and takes ramp fallback. */
export type DimensionSlice = {
  id: string;
  label: string;
  total: number;
  /** Projects in the slice. Absent on a slice built before measures existed
   *  (a test fixture) — read it as `total`. */
  count?: number;
  /** Under several Measure fields on a chart that draws them as SERIES (the
   *  bar), every field's figure for this slice, in `measures` order; `total`
   *  is then the first. Absent otherwise. */
  values?: Array<number | null>;
  color?: Color | null;
  /** The category ids a MERGED bucket stands for, so a drilldown can query the
   *  rows behind it. A slice that is one category carries none — its own `id`
   *  is the answer. */
  ids?: readonly string[];
};

/** The built-in dimensions as select options — one vocabulary across every
 * dimension-sliced widget's settings. The Group by select is this list plus
 * the workspace's groupable fields (`useDimensionFieldOptions`). */
export const WIDGET_DIMENSION_OPTIONS: { value: BuiltinWidgetDimension; label: string }[] = [
  { value: "type", label: "Type" },
  { value: "workflow", label: "Workflow" },
  { value: "stage", label: "Stage" },
  { value: "stageType", label: "Stage category" },
  { value: "team", label: "Team" },
  { value: "assignee", label: "Assignee" },
  { value: "age", label: "Follow up" },
  { value: "outcome", label: "Outcome" },
  { value: "source", label: "Source" },
];

/** What the kept no-value slice is called, per dimension: "No <noun>" for a
 *  built-in, "Unassigned" for the assignee (the word the rest of the app uses
 *  for a project nobody holds), "No <field name>" for a custom field. */
export function unassignedLabel(dimension: WidgetDimension, fieldName: string | null): string {
  if (isFieldDimension(dimension)) return fieldName ? `No ${fieldName}` : "No value";
  switch (dimension) {
    case "assignee":
      return "Unassigned";
    case "type":
      return "No type";
    case "workflow":
      return "No workflow";
    case "stage":
    case "stageType":
      return "No stage";
    case "team":
      return "No team";
    case "age":
      // The null status IS updates-off — the same word every clock surface uses.
      return AGE_STATUS_CONFIG.disabled.label;
    case "outcome":
      return "No outcome";
    case "source":
      return "No source";
  }
}

/** Issue `groupBy` per built-in dimension. `outcome` is absent on purpose — it
 *  reads the activity aggregation instead (see `isOutcomeDimension`). */
const GROUP_BY: Record<Exclude<BuiltinWidgetDimension, "outcome">, string> = {
  type: "issueTypeId",
  workflow: "workflowId",
  stage: "stageId",
  // Folded client-side into categories — see `isStageTypeDimension`.
  stageType: "stageId",
  // The project's own Assigned Team (`issue_team`) — never the assignees'
  // teams, which double-count multi-team members and shift with rosters.
  team: "teams",
  assignee: "assignees.entityId",
  age: "age.status",
  source: "issueSourceId",
};

/** The server `groupBy` for a project-counting dimension: a field token is its
 *  own; a built-in maps through `GROUP_BY`. */
export function issueGroupBy(dimension: Exclude<WidgetDimension, "outcome">): string {
  return isFieldDimension(dimension) ? dimension : GROUP_BY[dimension];
}

/** The server token that buckets Update activities by the outcome they recorded. */
const OUTCOME_GROUP_BY = "outcome";

/** One shared empty ask, so an unmeasured card's query variables never churn. */
const NO_MEASURES: readonly FieldMeasure[] = [];

/** A date-grained bucket key ("2026-06-01") as its period label. */
function periodLabel(key: string, grain: FieldDateGrain): string | null {
  const date = parseISO(key);
  if (Number.isNaN(date.getTime())) return null;
  if (grain === "month") return format(date, "MMM yyyy");
  if (grain === "week") return `Week of ${format(date, "MMM d, yyyy")}`;
  return format(date, "MMM d, yyyy");
}

/**
 * Counts projects matching `where`, grouped by the chosen dimension, ranked
 * high→low. The shared data layer behind every dimension-sliced widget
 * (portfolio-map's tiles, type-mix's slices): it fans the server `groupBy`
 * through `useIssueGroupCounts` and resolves the returned keys to display
 * labels via the workspace maps (an assignee may be a user OR a team, so both
 * are tried). `__unassigned__` is dropped — these charts show the categorized
 * work. Ids that resolve to nothing merge into ONE `__unresolved__` bucket.
 * The caller owns the `where` fold (scope + its own filters).
 *
 * With `measures`, each slice's `total` is the FIRST field's aggregate for the
 * bucket, `values` every field's (when there are several), and `count` the
 * projects behind it. A bucket whose projects hold no value for the first field
 * has nothing to plot and is dropped: a zero would read as "measured zero", and
 * would win a "Lowest" chart outright. The `outcome` dimension counts Updates,
 * which carry no field, so it ignores the measures. A null `dimension` (a
 * Group by naming a field the workspace no longer has) asks nothing and
 * answers an empty, ready set.
 *
 * `keepUnassigned` keeps the rows that carry no value for the dimension as
 * one grey slice ("No stage", "Unassigned", "No value") instead of dropping
 * them — the bar's "Show projects without a value" toggle. It ranks with the
 * rest by figure; a period axis puts it last, after the dated periods.
 */
export function useDimensionCounts(
  dimension: WidgetDimension | null,
  where: Record<string, unknown> | undefined,
  activityWhere?: Record<string, unknown>,
  measures: readonly FieldMeasure[] = NO_MEASURES,
  keepUnassigned = false,
) {
  const outcome = dimension !== null && isOutcomeDimension(dimension);
  const fn: MeasureFn | null = outcome ? null : (measures[0]?.fn ?? null);
  // BOTH readers mount every render — hook order is fixed, so the branch picks
  // a RESULT, never which hook runs. The unused one costs no query: issue
  // counts skip on an undefined `groupBy`, activity counts on their own `skip`.
  const issueCounts = useIssueGroupCounts({
    where,
    groupBy: outcome || dimension === null ? undefined : issueGroupBy(dimension),
    measures: outcome ? NO_MEASURES : measures,
  });
  const outcomeCounts = useActivityGroupCounts({
    groupBy: OUTCOME_GROUP_BY,
    where: activityWhere,
    skip: !outcome,
  });
  const { counts, ready, error } = outcome ? outcomeCounts : issueCounts;
  const values = outcome ? undefined : issueCounts.values;
  const { stageMap, issueTypeMap, teamMap, workflowMap, issueSourceMap, reasons, customFields } =
    useWorkspaceData();
  // Assignee keys are whichever members/teams the aggregation counted, so they
  // resolve through the DIRECTORY rather than `userMap` — the bootstrap page
  // answers for 500 members in name order, so the alphabet decided whose slice
  // got a name and whose fell into the unresolved bucket.
  const resolveMember = useMemberEntityResolver();
  // Outcome keys arrive as reason ids — the pool is small and workspace-global,
  // so one map serves every outcome-sliced widget on the board.
  const reasonMap = useMemo(
    () => new Map(reasons.map((reason) => [reason.reasonId, reason])),
    [reasons],
  );
  // A field dimension's keys are the field's stored values: option values,
  // booleans, member ids, region codes, or period starts for a date grain.
  const fieldGroup = useMemo(() => {
    if (dimension === null || !isFieldDimension(dimension)) return null;
    const token = parseFieldGroupToken(dimension);
    if (!token) return null;
    const field = customFields.find((candidate) => candidate.customFieldId === token.fieldId);
    if (!field) return null;
    const options = new Map(
      (field.config?.options ?? []).flatMap((option) => {
        const value = option?.value ?? option?.label;
        return value ? [[value, option] as const] : [];
      }),
    );
    return { field, grain: token.grain, options };
  }, [dimension, customFields]);

  const data = useMemo<DimensionSlice[]>(() => {
    if (dimension === null) return [];
    // A nullish answer means the id names nothing this workspace can print — an
    // id that resolves to no entity, or an entity whose own name is null. The
    // caller folds those together rather than printing a row per unnamed id.
    const label = (id: string): string | null | undefined => {
      if (fieldGroup) {
        if (fieldGroup.grain) return periodLabel(id, fieldGroup.grain);
        switch (fieldGroup.field.type) {
          case "checkbox":
            return id === "true" ? "Yes" : "No";
          case "member": {
            const member = resolveMember(id);
            if (member?.kind === "team") return member.team.teamName;
            return member?.kind === "user" ? member.user?.name : null;
          }
          default:
            // A select value the field no longer lists (an option since
            // deleted) is still what the project says — print it as stored.
            return fieldGroup.options.get(id)?.label ?? id;
        }
      }
      if (isFieldDimension(dimension)) return null;
      switch (dimension) {
        case "type":
          return issueTypeMap.get(id)?.name;
        case "workflow":
          return workflowMap.get(id)?.name;
        case "stage":
          return stageMap.get(id)?.name;
        case "stageType":
          return stageTypeLabel(id);
        case "team":
          return teamMap.get(id)?.teamName;
        case "assignee": {
          // The directory answers with a member or a team — the two kinds an
          // assignee key can name.
          const member = resolveMember(id);
          if (member?.kind === "team") return member.team.teamName;
          return member?.kind === "user" ? member.user?.name : null;
        }
        case "age":
          return AGE_STATUS_CONFIG[id as AgeStatus]?.label;
        case "outcome":
          return reasonMap.get(id)?.name;
        case "source":
          return issueSourceMap.get(id)?.name;
      }
    };
    // The stored icon color is an open string on the wire; the maps only ever
    // hold palette names, so the narrowing cast is safe.
    const color = (id: string): Color | null => {
      if (fieldGroup) {
        if (fieldGroup.field.type === "member") {
          const member = resolveMember(id);
          return member?.kind === "team" ? ((member.team.icon?.color as Color) ?? null) : null;
        }
        // An option's color is the field's own categorical palette; a field
        // that hides its option colors shows none here either.
        if (fieldGroup.field.config?.showOptionColors === false) return null;
        const stored = fieldGroup.options.get(id)?.color;
        if (stored == null) return null;
        const optionColor = asOptionColor(stored);
        return optionColor === NO_COLOR ? null : optionColor;
      }
      if (isFieldDimension(dimension)) return null;
      switch (dimension) {
        case "type":
          return (issueTypeMap.get(id)?.icon?.color as Color) ?? null;
        case "workflow":
          return (workflowMap.get(id)?.icon?.color as Color) ?? null;
        case "stage":
          return (stageMap.get(id)?.icon?.color as Color) ?? null;
        case "stageType":
          return isStageType(id) ? STAGE_TYPE_COLOR[id] : null;
        case "team":
          return (teamMap.get(id)?.icon?.color as Color) ?? null;
        case "assignee": {
          // A user assignee has no owned color (avatars, not icons); a team
          // assignee wears its team color.
          const member = resolveMember(id);
          return member?.kind === "team"
            ? ((member.team.icon?.color as Color) ?? null)
            : null;
        }
        case "age":
          return AGE_STATUS_CONFIG[id as AgeStatus]?.decoColor ?? null;
        case "outcome": {
          // An outcome's color is its TONE, not an icon color — the pool wears
          // green/grey/red so a chart of outcomes reads as good/neutral/bad at
          // a glance rather than as an arbitrary ramp.
          const tone = reasonMap.get(id)?.tone;
          return tone === undefined ? null : OUTCOME_TONE_CONFIG[normalizeOutcomeTone(tone)].swatch;
        }
        case "source":
          // A source is a plain name (no icon, no color); the chart's ramp colors it.
          return null;
      }
    };
    // Each bucket as the reader saw it: its project count and, under measures,
    // the server's aggregates. A bucket with no value for the first field drops.
    type Bucket = { id: string; count: number; value: number | null; values: Array<number | null> };
    const several = measures.length > 1;
    const buckets: Bucket[] = [];
    for (const [id, count] of Object.entries(counts)) {
      const figures = fn ? (values?.[id] ?? []) : [];
      const value = fn ? (figures[0] ?? null) : null;
      if (fn && value === null) continue;
      buckets.push({ id, count, value, values: figures });
    }
    const figure = (bucket: Bucket) => (fn ? (bucket.value ?? 0) : bucket.count);
    const fold = (parts: Bucket[]) =>
      foldMeasure(
        fn,
        parts.map((part) => ({ total: part.count, count: part.count, value: part.value })),
      ) ?? 0;
    // Every field's figure for a merged bucket, folded index by index.
    const foldAll = (parts: Bucket[]): Array<number | null> | undefined =>
      several
        ? measures.map((_, index) =>
            foldMeasure(
              fn,
              parts.map((part) => ({ total: part.count, count: part.count, value: part.values[index] ?? null })),
            ),
          )
        : undefined;
    const withValues = (slice: DimensionSlice, all: Array<number | null> | undefined): DimensionSlice =>
      all ? { ...slice, values: all } : slice;
    // The one slice for every row without a value, under the sentinel id the
    // server answered with — a drilldown can tell it apart from a real bucket.
    // Coloured like the unresolved bucket: neither is a category of its own.
    const unassignedSlice = (parts: Bucket[]): DimensionSlice[] => {
      if (!keepUnassigned || parts.length === 0) return [];
      const count = parts.reduce((sum, part) => sum + part.count, 0);
      if (count === 0) return [];
      return [
        withValues(
          {
            id: UNASSIGNED_DIMENSION_KEY,
            total: fold(parts),
            count,
            label: unassignedLabel(dimension, fieldGroup?.field.name ?? null),
            color: null,
          },
          foldAll(parts),
        ),
      ];
    };
    const isUnassigned = (bucket: Bucket) =>
      bucket.id === UNASSIGNED_DIMENSION_KEY || bucket.id === NO_OUTCOME_KEY;

    if (isStageTypeDimension(dimension)) {
      // Fold the per-stage answer into its categories. A stage id the map
      // cannot place (a stage deleted since the count) has no category and
      // is dropped with the unassigned bucket — these charts show the
      // categorized work.
      const byType = new Map<StageType, { parts: typeof buckets; ids: string[] }>(
        STAGE_TYPE_ORDER.map((type) => [type, { parts: [], ids: [] }]),
      );
      for (const bucket of buckets) {
        const type = stageMap.get(bucket.id)?.type;
        if (!isStageType(type)) continue;
        const category = byType.get(type);
        if (!category) continue;
        category.parts.push(bucket);
        category.ids.push(bucket.id);
      }
      const categorized = STAGE_TYPE_ORDER.flatMap<DimensionSlice>((type) => {
        const category = byType.get(type);
        if (!category || category.parts.length === 0) return [];
        const count = category.parts.reduce((sum, part) => sum + part.count, 0);
        if (count === 0) return [];
        return [
          withValues(
            {
              id: type,
              ids: category.ids,
              total: fold(category.parts),
              count,
              label: label(type) ?? type,
              color: color(type),
            },
            foldAll(category.parts),
          ),
        ];
      });
      return [...categorized, ...unassignedSlice(buckets.filter(isUnassigned))];
    }
    const named: DimensionSlice[] = [];
    const unresolved: typeof buckets = [];
    const unassigned: typeof buckets = [];
    for (const bucket of buckets) {
      // Both sentinels mean "this row carries no value for the dimension" —
      // an issue with no stage, an Update posted with no outcome. These charts
      // show the categorized work, so the bucket is dropped unless the widget
      // asked to keep it.
      if (isUnassigned(bucket)) {
        unassigned.push(bucket);
        continue;
      }
      const name = label(bucket.id);
      if (name == null) {
        unresolved.push(bucket);
        continue;
      }
      named.push(
        withValues(
          {
            id: bucket.id,
            total: figure(bucket),
            count: bucket.count,
            label: name,
            color: color(bucket.id),
          },
          several ? bucket.values : undefined,
        ),
      );
    }
    if (unresolved.length > 0) {
      named.push(
        withValues(
          {
            id: UNRESOLVED_DIMENSION_KEY,
            ids: unresolved.map((bucket) => bucket.id),
            total: fold(unresolved),
            count: unresolved.reduce((sum, bucket) => sum + bucket.count, 0),
            label: "Unknown",
            color: null,
          },
          foldAll(unresolved),
        ),
      );
    }
    // A period axis reads in calendar order, whatever the figures, the undated
    // rows after the last period; every other dimension ranks high→low.
    if (fieldGroup?.grain) {
      return [...named.sort((a, b) => a.id.localeCompare(b.id)), ...unassignedSlice(unassigned)];
    }
    return [...named, ...unassignedSlice(unassigned)].sort((a, b) => b.total - a.total);
  }, [
    counts,
    values,
    fn,
    measures,
    dimension,
    keepUnassigned,
    fieldGroup,
    stageMap,
    issueTypeMap,
    teamMap,
    resolveMember,
    workflowMap,
    issueSourceMap,
    reasonMap,
  ]);

  return { data, loading: dimension !== null && !ready, error };
}
